import { getMandates, Mandate } from "@/api/openapi";
import { PspContext } from "@/config/contexts";
import { translate } from "@/config/translator";
import {
  setAccessTokenStorage,
  setCookieConsentStorage,
  setRefreshTokenStorage,
  setSelectedMandateIdStorage,
} from "@/config/utils";
import {
  Dispatch,
  KeyboardEvent,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

type UseUserDetailsPopupParams = {
  setIsOpen: Dispatch<SetStateAction<boolean>>;
};

export function useUserDetailsPopup({ setIsOpen }: UseUserDetailsPopupParams) {
  const {
    lang,
    user,
    selectedMandate,
    setSelectedMandate,
    setSelectedMandateId,
    setUser,
    msalInstance,
    setCookieConsentDialogOpen,
    setCookieConsentFunctionalOnly,
  } = useContext(PspContext);
  const navigate = useNavigate();

  const mandateSearchInputRef = useRef<HTMLInputElement>(null);

  const [mandateSearchQuery, setMandateSearchQuery] = useState("");
  const [isLoadingEmployeeMandates, setIsLoadingEmployeeMandates] =
    useState(false);
  const [employeeMandates, setEmployeeMandates] = useState<Mandate[]>();

  useEffect(() => {
    if (
      user?.id &&
      (user.scopes.includes("admin") || user.scopes.includes("employee"))
    ) {
      setIsLoadingEmployeeMandates(true);
      getMandates({
        query: { limit: 100, offset: 0, query: mandateSearchQuery },
      }).then(
        ({ data }) => {
          setEmployeeMandates(data);
          setIsLoadingEmployeeMandates(false);
        },
        () => {
          toast(translate("couldntLoadMandates", lang));
          setIsLoadingEmployeeMandates(false);
        },
      );
    }
  }, [mandateSearchQuery, lang, user?.id, user?.scopes]);

  useEffect(() => {
    const mandateSearchInput = mandateSearchInputRef.current;
    if (!mandateSearchInput) return;
    mandateSearchInput.focus();
  }, []);

  function onSelectMandate(mandate: Mandate) {
    // context
    setSelectedMandateId(mandate.id);
    setSelectedMandate(mandate);

    setSelectedMandateIdStorage(mandate.id);

    navigate("/home");
  }

  async function onLogout() {
    setCookieConsentStorage(null);
    setCookieConsentFunctionalOnly(undefined);
    setAccessTokenStorage(null);
    setRefreshTokenStorage(null);
    setUser(undefined);
    setSelectedMandateId(null);
    setSelectedMandateIdStorage(null);
    setSelectedMandate(undefined);
    await msalInstance?.initialize();
    msalInstance?.setActiveAccount(null);
    navigate("/login");
  }

  function mandateSearchInputOnKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (
      event.key === "Enter" &&
      employeeMandates &&
      employeeMandates.length !== 0
    ) {
      setIsOpen(false);
      onSelectMandate(employeeMandates[0]);
    }
  }

  return {
    lang,
    user,
    mandateSearchQuery,
    setMandateSearchQuery,
    onLogout,
    onSelectMandate,
    selectedMandate,
    isLoadingEmployeeMandates,
    employeeMandates,
    navigate,
    mandateSearchInputRef,
    mandateSearchInputOnKeyDown,
  };
}
